import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, RefreshCw, CheckCircle2, Circle, AlertCircle, BookOpen, Type, Languages, Megaphone, Copy } from "lucide-react";

const AREA_META = {
  story: { label: "Story", Icon: BookOpen, accent: "var(--bf-primary)" },
  style: { label: "Style & prose", Icon: Type, accent: "var(--bf-accent)" },
  grammar: { label: "Grammar", Icon: Languages, accent: "#0EA5E9" },
  marketability: { label: "Marketability", Icon: Megaphone, accent: "#7C3AED" },
};

const PRIORITY_BADGES = {
  high: { label: "high", style: { background: "#FEE2E2", color: "#991B1B" } },
  medium: { label: "med", style: { background: "#FEF3C7", color: "#92400E" } },
  low: { label: "low", style: { background: "#DBEAFE", color: "#1E40AF" } },
};

function ScoreRing({ score, size = 96 }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const color = pct >= 90 ? "#10B981" : pct >= 70 ? "var(--bf-primary)" : pct >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div
      className="rounded-full flex items-center justify-center font-serif"
      style={{
        width: size, height: size,
        background: `conic-gradient(${color} ${pct * 3.6}deg, var(--bf-surface) 0deg)`,
      }}
    >
      <div
        className="rounded-full flex items-center justify-center"
        style={{ width: size - 16, height: size - 16, background: "white" }}
      >
        <span style={{ fontSize: size * 0.32, fontWeight: 600, color }}>{pct}</span>
      </div>
    </div>
  );
}

export default function ReviewPanel({ bookId }) {
  const [review, setReview] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/books/${bookId}/review`);
      setReview(data.review);
      setGeneratedAt(data.generated_at);
    } catch {
      toast.error("Failed to load review");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [bookId]);

  const runReview = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/books/${bookId}/review`, {});
      setReview(data);
      setGeneratedAt(data.generated_at);
      toast.success("Review complete");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Review failed");
    } finally {
      setRunning(false);
    }
  };

  const setRecStatus = async (rec_id, status) => {
    try {
      await api.patch(`/books/${bookId}/review/recommendations/${rec_id}`, { status });
      setReview((r) => ({
        ...r,
        recommendations: r.recommendations.map((rec) => rec.rec_id === rec_id ? { ...rec, status } : rec),
      }));
    } catch {
      toast.error("Failed");
    }
  };

  const copyBlurb = (text) => {
    navigator.clipboard.writeText(text || "");
    toast.success("Blurb copied");
  };

  if (loading) return <p className="text-sm" style={{ color: "var(--bf-text-muted)" }} data-testid="review-loading">Loading…</p>;

  return (
    <div className="max-w-4xl space-y-10" data-testid="review-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-3xl mb-1">Review & recommendations</h2>
          <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
            AI editorial pass covering story, style, grammar and marketability. Required before publishing.
          </p>
          {generatedAt && (
            <p className="text-xs mt-1" style={{ color: "var(--bf-text-muted)" }} data-testid="review-generated-at">
              Last run: {new Date(generatedAt).toLocaleString()}
            </p>
          )}
        </div>
        <Button
          onClick={runReview}
          disabled={running}
          className="text-white"
          style={{ background: "var(--bf-primary)" }}
          data-testid="review-run-btn"
        >
          {running ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {running ? "Running…" : review ? "Re-run review" : "Run review"}
        </Button>
      </div>

      {!review && !running && (
        <div
          className="p-8 rounded-lg text-center"
          style={{ background: "var(--bf-surface)", border: "1px dashed var(--bf-border)" }}
          data-testid="review-empty"
        >
          <AlertCircle className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--bf-accent)" }} />
          <h3 className="font-serif text-2xl mb-2">No review yet</h3>
          <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
            Click &quot;Run review&quot; to get an editorial score, area-by-area notes, a back-cover blurb suggestion and 7-10 concrete recommendations.
          </p>
        </div>
      )}

      {review && (
        <>
          <section className="flex items-start gap-6 p-6 rounded-lg" style={{ background: "white", border: "1px solid var(--bf-border)" }} data-testid="review-overall">
            <ScoreRing score={review.overall_score} />
            <div className="flex-1">
              <div className="label-eyebrow mb-2">Overall</div>
              <p className="font-serif text-xl leading-snug" data-testid="review-summary">{review.summary}</p>
            </div>
          </section>

          <section className="grid sm:grid-cols-2 gap-4" data-testid="review-areas">
            {["story", "style", "grammar", "marketability"].map((key) => {
              const area = review.areas?.[key] || {};
              const { label, Icon, accent } = AREA_META[key];
              return (
                <div
                  key={key}
                  className="p-5 rounded-lg"
                  style={{ background: "white", border: "1px solid var(--bf-border)" }}
                  data-testid={`review-area-${key}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: accent }} />
                      <span className="font-serif text-lg">{label}</span>
                    </div>
                    <span className="text-2xl font-serif" style={{ color: accent }}>{area.score || 0}</span>
                  </div>
                  <ul className="space-y-1.5 text-sm" style={{ color: "var(--bf-text-muted)" }}>
                    {(area.notes || []).map((n, i) => (
                      <li key={`${key}-note-${i}-${String(n).slice(0, 24)}`} className="flex gap-2"><span style={{ color: accent }}>•</span><span>{n}</span></li>
                    ))}
                  </ul>
                  {key === "marketability" && (area.back_cover_blurb || area.target_reader || area.comparable_titles?.length) && (
                    <div className="mt-4 pt-3 border-t space-y-2" style={{ borderColor: "var(--bf-border)" }}>
                      {area.target_reader && (
                        <p className="text-xs"><span className="label-eyebrow">Target reader · </span>{area.target_reader}</p>
                      )}
                      {area.comparable_titles?.length > 0 && (
                        <p className="text-xs"><span className="label-eyebrow">Comparable titles · </span>{area.comparable_titles.join(" / ")}</p>
                      )}
                      {area.back_cover_blurb && (
                        <div className="text-sm p-3 rounded mt-2" style={{ background: "var(--bf-surface)" }} data-testid="review-back-cover-blurb">
                          <div className="flex justify-between items-start gap-2 mb-2">
                            <span className="label-eyebrow">Back-cover blurb</span>
                            <button onClick={() => copyBlurb(area.back_cover_blurb)} className="text-xs underline" data-testid="review-copy-blurb">
                              <Copy className="inline w-3 h-3 mr-1" /> Copy
                            </button>
                          </div>
                          <p className="font-serif italic">{area.back_cover_blurb}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          <section data-testid="review-recommendations">
            <h3 className="font-serif text-2xl mb-3">Recommendations</h3>
            <ul className="space-y-2">
              {(review.recommendations || []).map((r) => (
                <li
                  key={r.rec_id}
                  className="p-3 rounded flex items-start gap-3"
                  style={{ background: "white", border: "1px solid var(--bf-border)" }}
                  data-testid={`review-rec-${r.rec_id}`}
                >
                  <button
                    onClick={() => setRecStatus(r.rec_id, r.status === "done" ? "open" : "done")}
                    className="flex-shrink-0 mt-0.5"
                    data-testid={`review-rec-toggle-${r.rec_id}`}
                  >
                    {r.status === "done"
                      ? <CheckCircle2 className="w-5 h-5" style={{ color: "var(--bf-primary)" }} />
                      : <Circle className="w-5 h-5" style={{ color: "var(--bf-text-muted)" }} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge style={PRIORITY_BADGES[r.priority]?.style || PRIORITY_BADGES.medium.style} data-testid={`review-rec-priority-${r.rec_id}`}>
                        {PRIORITY_BADGES[r.priority]?.label || r.priority}
                      </Badge>
                      <span className="text-xs label-eyebrow">{AREA_META[r.area]?.label || r.area}</span>
                    </div>
                    <p
                      className={`text-sm ${r.status === "done" ? "line-through" : ""}`}
                      style={{ color: r.status === "done" ? "var(--bf-text-muted)" : "inherit" }}
                    >
                      {r.text}
                    </p>
                  </div>
                  {r.status !== "skipped" && (
                    <button
                      onClick={() => setRecStatus(r.rec_id, "skipped")}
                      className="text-xs"
                      style={{ color: "var(--bf-text-muted)" }}
                      data-testid={`review-rec-skip-${r.rec_id}`}
                    >
                      skip
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
